import React, { useEffect, useState, forwardRef } from "react";

import { useTranslation } from "next-i18next";
import SafeArea from "@/components/safe-area";

import { SearchInput, SelectHolder, SelectView, SelectedItem } from "@/components/search-area";
import { Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle, DrawerTrigger } from "../ui/drawer";
import useMobile from "@/hooks/useMobile";

import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

interface Props<T> {
	options: T[];
	onChange: (val: string) => void; // Controller filed.onChange方法
	value: string;
	defaultValue?: string;
	placeholder?: string;
	labelLeft?: React.ReactNode | string;
	name?: string; // react-hook-form必须传
	placeholderTextClassName?: string;
	disabled?: boolean;
	renderOption?: (option: T) => React.ReactNode;
	hiddenSearch?: boolean;
	resetSelectHolder?: React.ReactNode;
	triggerClassName?: string;
	iconClassName?: string;
}

const SelectArea = forwardRef<HTMLSelectElement, Props<SelectedItem>>((props, ref) => {
	const [selectItem, setSelectItem] = useState<SelectedItem>();
	const [searchText, setSearchText] = useState("");
	const [visible, setVisible] = useState(false);
	const { t } = useTranslation("common");
	const isMobile = useMobile();

	useEffect(() => {
		const defaultSelectItem = props.options.find(
			(item) => item.value === props.defaultValue || item.value === props.value,
		);
		if (defaultSelectItem) setSelectItem(defaultSelectItem);
		else setSelectItem(undefined);
	}, [props.defaultValue, props.options, props.value]);

	const filterOptions = (filterText: string, defaultOptions: SelectedItem[]) => {
		if (!filterText) return defaultOptions;
		return defaultOptions.filter((item) => {
			return (
				item.label.toLocaleLowerCase().includes(searchText.toLocaleLowerCase()) ||
				item.value.toLocaleLowerCase().includes(searchText.toLocaleLowerCase())
			);
		});
	};

	const FilterContent = (
		<div className="mx-auto w-full">
			{!props.hiddenSearch && (
				<SearchInput
					placeholder={t("search")}
					value={searchText}
					onClear={() => setSearchText("")}
					onChange={(val) => setSearchText(val)}
				/>
			)}

			<SelectView
				list={filterOptions(searchText, props.options)}
				onSelected={(e) => {
					setSelectItem(e);
					// 其实就是最外层 field.onChange 方法
					props.onChange(e.value);
					setVisible(false);
				}}
				renderOption={props.renderOption}
			/>
		</div>
	);

	if (isMobile)
		return (
			<div ref={ref as any}>
				{props.resetSelectHolder ? (
					<div
						onClick={() => {
							if (props.disabled) return;
							setVisible(true);
						}}
					>
						{props.resetSelectHolder}
					</div>
				) : (
					<SelectHolder
						text={selectItem?.label}
						placeholder={props.placeholder}
						onClick={() => {
							if (props.disabled) return;
							setVisible(true);
						}}
						labelLeft={props.labelLeft}
						disabled={props.disabled}
						className={props.triggerClassName}
						iconClassName={props.iconClassName}
					/>
				)}

				<Drawer open={visible} onClose={() => setVisible(false)}>
					<DrawerContent>
						<DrawerHeader className="py-2">
							<DrawerTitle></DrawerTitle>
							<DrawerDescription></DrawerDescription>
						</DrawerHeader>
						{FilterContent}
						<SafeArea position="bottom" />
					</DrawerContent>
				</Drawer>
			</div>
		);

	return (
		<div ref={ref as any}>
			<Popover open={visible} onOpenChange={(open) => !open && setVisible(false)}>
				<PopoverTrigger asChild>
					<div
						onClick={() => {
							if (props.disabled) return;
							if (!visible) setVisible(true);
						}}
					>
						{props.resetSelectHolder ? (
							props.resetSelectHolder
						) : (
							<SelectHolder
								text={selectItem?.label}
								placeholder={props.placeholder}
								onClick={() => {
									if (props.disabled) return;
									setVisible(true);
								}}
								labelLeft={props.labelLeft}
								disabled={props.disabled}
							/>
						)}
					</div>
				</PopoverTrigger>

				<PopoverContent align="start" className="w-80 px-0 py-5">
					{FilterContent}
				</PopoverContent>
			</Popover>
		</div>
	);
});

SelectArea.displayName = "SelectArea";
export default SelectArea;
